import {
  getTreeCommandCenterFeed,
  type TreeCommandCenterFeed,
} from './TreeCommandCenterFeed';

export type CanonicalTreeHistoricalAuthorityPresentationState =
  | 'REGISTRY_ONLY_AUTHORITY_CONFIRMED'
  | 'PHYSICAL_SOURCE_DELETION_CONFIRMED'
  | 'HISTORICAL_AUTHORITY_AVAILABLE'
  | 'HISTORICAL_AUTHORITY_INCONSISTENT';

export type CanonicalTreeHistoricalAuthorityPresentationTone =
  | 'confirmed'
  | 'informational'
  | 'warning';

export interface CanonicalTreeCommandCenterFeedHistoricalAuthorityPresentation {
  readonly milestone:
    'CC-TREE-ARCH-072';

  readonly presentation:
    'TREE_COMMAND_CENTER_FEED_HISTORICAL_AUTHORITY_PRESENTATION_PROJECTION';

  readonly presentationState:
    CanonicalTreeHistoricalAuthorityPresentationState;

  readonly tone:
    CanonicalTreeHistoricalAuthorityPresentationTone;

  readonly label:
    string;

  readonly detail:
    string;

  readonly sourceFeedGeneratedAt:
    string;

  readonly sourceReadModelMilestone:
    'CC-TREE-ARCH-068';

  readonly currentRepositoryAuthorityMilestone:
    'CC-TREE-ARCH-066';

  readonly consolidationAuthorityMilestone:
    'CC-TREE-ARCH-067';

  readonly currentPhysicalSourceState:
    'FOUR_RETIRED_SOURCES_ABSENT';

  readonly registryOnlyProductionAuthority:
    boolean;

  readonly immutableHistoricalTruthPreserved:
    boolean;

  readonly immutableArchiveAuthority:
    boolean;

  readonly historicalVerifierAuthorityPreserved:
    boolean;

  readonly historicalDocumentationAuthorityPreserved:
    boolean;

  readonly physicalRepositorySourceCount:
    number;

  readonly deletedHistoricalSourceCount:
    number;

  readonly activeProductionReferenceCount:
    number;

  readonly historicalAuthorityCount:
    number;

  readonly currentAuthorityCount:
    number;

  readonly milestoneCount:
    number;

  readonly arch056ArchiveChanged:
    boolean;

  readonly arch062ArchiveChanged:
    boolean;

  readonly certifiesTreeHealth:
    false;

  readonly certifiesReleaseReadiness:
    false;

  readonly certifiesDeploymentReadiness:
    false;

  readonly affectsRuntimeDecision:
    false;

  readonly affectsRuntimeGuard:
    false;

  readonly affectsOperationalCertification:
    false;

  readonly memberFacing:
    false;

  readonly workspaceWiringCompleted:
    false;

  readonly nextGovernedDependency:
    'TREE_OPERATIONS_WORKSPACE_HISTORICAL_AUTHORITY_PRESENTATION_WIRING';
}

const classifyPresentation = (
  feed:
    TreeCommandCenterFeed,
): Pick<
  CanonicalTreeCommandCenterFeedHistoricalAuthorityPresentation,
  'presentationState' |
  'tone' |
  'label' |
  'detail'
> => {
  const authority =
    feed.historicalAuthorityReadModel;

  const archivesUnchanged =
    !authority.arch056ArchiveChanged &&
    !authority.arch062ArchiveChanged;

  const historicalAuthorityPreserved =
    authority.immutableHistoricalTruthPreserved &&
    !authority.historicalAuthoritySourcesRewritten &&
    !authority.historicalDocumentationRewritten &&
    !authority.historicalVerifiersDeleted;

  const physicalDeletionConfirmed =
    authority.currentRepositoryAuthorityMilestone ===
      'CC-TREE-ARCH-066' &&
    authority.currentPhysicalSourceState ===
      'FOUR_RETIRED_SOURCES_ABSENT' &&
    authority.physicalRepositorySourceCount === 0 &&
    authority.deletedHistoricalSourceCount === 4;

  const productionAuthorityConfirmed =
    authority.registryOnlyProductionAuthority &&
    authority.activeProductionReferenceCount === 0;

  if (
    physicalDeletionConfirmed &&
    productionAuthorityConfirmed &&
    archivesUnchanged &&
    historicalAuthorityPreserved
  ) {
    return {
      presentationState:
        'REGISTRY_ONLY_AUTHORITY_CONFIRMED',

      tone:
        'confirmed',

      label:
        'Registry-only Tree authority confirmed',

      detail:
        'Four retired browser-runtime sources are absent, production authority is registry-only, and immutable historical evidence remains preserved.',
    };
  }

  if (
    physicalDeletionConfirmed &&
    archivesUnchanged &&
    historicalAuthorityPreserved
  ) {
    return {
      presentationState:
        'PHYSICAL_SOURCE_DELETION_CONFIRMED',

      tone:
        'informational',

      label:
        'Historical source deletion confirmed',

      detail:
        'The governed retired sources are absent and immutable historical evidence remains available, but registry-only production authority is not fully confirmed.',
    };
  }

  if (
    archivesUnchanged &&
    historicalAuthorityPreserved
  ) {
    return {
      presentationState:
        'HISTORICAL_AUTHORITY_AVAILABLE',

      tone:
        'informational',

      label:
        'Historical Tree authority available',

      detail:
        'Immutable historical authority remains available, but the current repository-source disposition is not fully confirmed.',
    };
  }

  return {
    presentationState:
      'HISTORICAL_AUTHORITY_INCONSISTENT',

    tone:
      'warning',

    label:
      'Historical Tree authority inconsistent',

    detail:
      'The historical-authority read model contains an inconsistent archive, verifier, documentation, source, or production-authority disposition.',
  };
};

export const projectCanonicalTreeCommandCenterFeedHistoricalAuthorityPresentation =
  (
    feed:
      TreeCommandCenterFeed =
        getTreeCommandCenterFeed(),
  ): CanonicalTreeCommandCenterFeedHistoricalAuthorityPresentation => {
    const authority =
      feed.historicalAuthorityReadModel;

    const classification =
      classifyPresentation(feed);

    const immutableArchiveAuthority =
      authority.immutableHistoricalTruthPreserved &&
      !authority.arch056ArchiveChanged &&
      !authority.arch062ArchiveChanged;

    const historicalVerifierAuthorityPreserved =
      !authority.historicalVerifiersDeleted;

    const historicalDocumentationAuthorityPreserved =
      !authority.historicalDocumentationRewritten;

    return Object.freeze({
      milestone:
        'CC-TREE-ARCH-072',

      presentation:
        'TREE_COMMAND_CENTER_FEED_HISTORICAL_AUTHORITY_PRESENTATION_PROJECTION',

      ...classification,

      sourceFeedGeneratedAt:
        feed.generatedAt,

      sourceReadModelMilestone:
        authority.milestone,

      currentRepositoryAuthorityMilestone:
        authority.currentRepositoryAuthorityMilestone,

      consolidationAuthorityMilestone:
        authority.consolidationAuthorityMilestone,

      currentPhysicalSourceState:
        authority.currentPhysicalSourceState,

      registryOnlyProductionAuthority:
        authority.registryOnlyProductionAuthority,

      immutableHistoricalTruthPreserved:
        authority.immutableHistoricalTruthPreserved,

      immutableArchiveAuthority,

      historicalVerifierAuthorityPreserved,

      historicalDocumentationAuthorityPreserved,

      physicalRepositorySourceCount:
        authority.physicalRepositorySourceCount,

      deletedHistoricalSourceCount:
        authority.deletedHistoricalSourceCount,

      activeProductionReferenceCount:
        authority.activeProductionReferenceCount,

      historicalAuthorityCount:
        authority.historicalAuthorityCount,

      currentAuthorityCount:
        authority.currentAuthorityCount,

      milestoneCount:
        authority.milestoneCount,

      arch056ArchiveChanged:
        authority.arch056ArchiveChanged,

      arch062ArchiveChanged:
        authority.arch062ArchiveChanged,

      certifiesTreeHealth:
        false,

      certifiesReleaseReadiness:
        false,

      certifiesDeploymentReadiness:
        false,

      affectsRuntimeDecision:
        false,

      affectsRuntimeGuard:
        false,

      affectsOperationalCertification:
        false,

      memberFacing:
        false,

      workspaceWiringCompleted:
        false,

      nextGovernedDependency:
        'TREE_OPERATIONS_WORKSPACE_HISTORICAL_AUTHORITY_PRESENTATION_WIRING',
    });
  };
